import { useQuery } from '@tanstack/vue-query'
import type { Ref } from 'vue'
import { computed } from 'vue'

import { getGroups, GROUPS_QUERY_KEY } from '@/features/groups/api'
import type { GetGroupsResponseType, V2GroupType } from '@/features/groups/types'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import { MAX_GROUPS_PER_PAGE } from '@/utils/config/constants'
import authStore from '@/features/auth/stores/auth/auth.store'
import { selectGetUserActivePermissionGroup } from '@/features/auth/stores/auth/auth.selectors'
import { PermissionsGroupsEnumType } from '@/features/auth/types'

function V1ToV2DataMiddleware({ data }: { data: GetGroupsResponseType | undefined }): V2GroupType[] {
  const groups = data?.groups?.map((group) => {
    return {
      id: group.id,
      idByMentor: group.idByMentor,
      conversationId: group.conversationId,
      meetingRoomId: group.meetingRoomId,
      programId: group.programId,
      name: group.customName ?? group.name,
      users: group.groupUsers.map((groupUser) => ({
        id: groupUser.user.id,
        firstName: groupUser.user.identity?.firstname,
        lastName: groupUser.user.identity?.lastname,
        picture: groupUser.user.personalInformation?.profilPicture,
        email: groupUser.user.email,
        roles: groupUser.roles,
      })),
      completedSessions: group.sessions.length,
    } satisfies V2GroupType
  })

  return groups || []
}

const useGroups = ({
  pageRef,
  perPageRef,
  searchRef,
  nbMenteesRef,
}: {
  pageRef?: Ref<number>
  perPageRef?: Ref<number>
  searchRef?: Ref<string | null>
  nbMenteesRef?: Ref<string>
} = {}) => {
  const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)

  const userActivePermissionGroupRef = selectGetUserActivePermissionGroup(authStore)

  const getUsersPageRef = computed(() => pageRef?.value || 1)
  const getUsersPerPageRef = computed(() => perPageRef?.value || MAX_GROUPS_PER_PAGE)
  const getUsersSearchRef = computed(() => searchRef?.value || '')
  const getNbMenteesRef = computed(() => nbMenteesRef?.value || 'any')
  const getAsAdminRef = computed(() => userActivePermissionGroupRef.value === PermissionsGroupsEnumType.ADMIN)
  // Check if all the required data is available to make the query
  const isGroupsQueryEnabledRef = computed(() => !!selectedProgramIdRef.value)

  const groupsQuery = useQuery({
    queryKey: [
      GROUPS_QUERY_KEY,
      {
        programId: selectedProgramIdRef,
        search: getUsersSearchRef,
        perPage: getUsersPerPageRef,
        page: getUsersPageRef,
        nbMentees: getNbMenteesRef,
        asAdmin: getAsAdminRef,
      },
    ],
    queryFn: () =>
      getGroups({
        programId: selectedProgramIdRef.value!,
        page: getUsersPageRef.value!,
        search: getUsersSearchRef.value,
        perPage: getUsersPerPageRef.value,
        nbMentees: getNbMenteesRef.value,
        asAdmin: getAsAdminRef.value,
      }),
    enabled: isGroupsQueryEnabledRef,
  })

  const data = computed(() => {
    return V1ToV2DataMiddleware({
      data: groupsQuery.data.value,
    })
  })

  const totalPagesRef = computed(() => groupsQuery.data.value?.totalPages)
  const totalGroupsRef = computed(() => groupsQuery.data.value?.totalGroups)
  const maxMenteesPerGroupRef = computed(() => groupsQuery.data.value?.maxMenteesPerGroup)
  return {
    groupsRef: data,
    totalPagesRef,
    totalGroupsRef,
    maxMenteesPerGroupRef,
    isPendingRef: groupsQuery.isPending,
  }
}

export default useGroups
